import React, { useEffect, useState, useCallback, useRef } from 'react';

import {
  TextLineHeight,
  TextAppearance,
  TextSize,
} from '@vw-marketing/us-components';

import {
  Arrow,
  NavMenuContainer,
  NavHeader,
  NavLink,
  NavList,
  NavSection,
} from './styles';
import { createFeatureHubHref } from '../../utils/navigation-url';
import { FontWeight } from '../../utils/typography';
import { Icon } from '../Icon';
import { isSectionIndexable, sectionContains } from '../../utils/navigation';
import { NavigationSection, SectionModel } from '../../fetch-data/graphql';
import { NavItemProps, NavMenuProps } from './types';
import { normalizeSectionId, getSectionFromPath } from '../../utils/query-param-helpers';
import { onVWBasicAccordionOpenClickEvent } from '../../tagging/tagging-helpers';
import { resetQsgSearch } from '../../store/slices/qsg/actions';
import { TextBox } from '../Box';
import { TextTag, Breakpoints, useTheme } from '@volkswagen-onehub/components-core';
import { useFeatureServices } from '../../hooks/use-feature-services';
import { useHistory } from '../../hooks/use-history';
import { useStore } from '../../store';
import { useTrackingManager } from '../../hooks/use-tracking-manager';

export const NavMenu = ({ sections, onCloseSidebar }: NavMenuProps) => {
  const [state, dispatch] = useStore();
  const { qsgNavigation } = state.qsg;
  const trackingManager = useTrackingManager();
  const history = useHistory();

  const featureServices = useFeatureServices();
  const serverRequest = featureServices['s2:server-request'];
  const serverRequestUrl = serverRequest?.url;

  const currentSection = getSectionFromPath(history.location.pathname);

  const model = qsgNavigation
    ? {
      modelId: qsgNavigation.modelId,
      modelName: qsgNavigation.modelName,
      modelYear: qsgNavigation.modelYear,
    }
    : undefined;

  // Use path-based system for tracking open sections (e.g., "0", "0-1", "0-1-2")
  const [openPaths, setOpenPaths] = useState<Set<string>>(new Set());
  const [hoveredPath, setHoveredPath] = useState<string | null>(null);
  const [focusedPath, setFocusedPath] = useState<string | null>(null);

  // Recursively find all parent paths that contain the current section
  const findParentPaths = useCallback(
    (
      sections: NavigationSection[],
      targetId: string,
      currentPath: string = ''
    ): string[] => {
      const paths: string[] = [];
      for (let i = 0; i < sections.length; i++) {
        const section = sections[i];
        const path = currentPath ? `${currentPath}-${i}` : `${i}`;

        if (sectionContains(section, targetId)) {
          paths.push(path);
          if (section.subsections?.length) {
            paths.push(...findParentPaths(section.subsections, targetId, path));
          }
        }
      }
      return paths;
    },
    []
  );

  useEffect(() => {
    if (!currentSection || !sections?.length) return;

    const parentPaths = findParentPaths(sections, currentSection);
    setOpenPaths((prev) => {
      const merged = new Set([...prev, ...parentPaths]);
      return merged;
    });
  }, [currentSection, sections, findParentPaths]);

  const updateSection = (section: SectionModel | NavigationSection) => {
    dispatch(resetQsgSearch());
    onCloseSidebar(section);
  };

  return (
    <NavMenuContainer>
      {sections.map((section, index) => {
        const path = `${index}`;
        return (
          <NavItem
            key={section.sectionId || index}
            section={section}
            currentSection={currentSection}
            updateSection={updateSection}
            path={path}
            openPaths={openPaths}
            setOpenPaths={setOpenPaths}
            hoveredPath={hoveredPath}
            setHoveredPath={setHoveredPath}
            focusedPath={focusedPath}
            setFocusedPath={setFocusedPath}
            level={0}
            trackingManager={trackingManager}
            model={model}
            serverRequestUrl={serverRequestUrl}
          />
        );
      })}
    </NavMenuContainer>
  );
};

export const NavItem = ({
  section,
  currentSection,
  updateSection,
  path,
  openPaths,
  setOpenPaths,
  hoveredPath,
  setHoveredPath,
  focusedPath,
  setFocusedPath,
  level,
  trackingManager,
  model,
  serverRequestUrl,
}: NavItemProps) => {
  const history = useHistory();
  const theme = useTheme();
  const hasSubsections = section.subsections && section.subsections.length > 0;
  const isMouseInteractionRef = useRef(false);

  const isIndexable = isSectionIndexable(section);
  const isOpen = openPaths.has(path);
  const isHovered = hoveredPath === path;
  const isFocused = focusedPath === path;
  // Normalize both for comparison to handle spacing differences
  const normalizedCurrentSection = normalizeSectionId(currentSection);
  const normalizedSectionId = normalizeSectionId(section.sectionId);
  const isCurrent = normalizedCurrentSection === normalizedSectionId;
  const isParentOfCurrentSection = sectionContains(section, currentSection);

  const highlightTab =
    isHovered || isFocused || isCurrent || isParentOfCurrentSection;
  const highlightBackground = isFocused || isCurrent;

  const handleMouseDown = () => {
    isMouseInteractionRef.current = true;
    // Reset after a short delay to allow focus event to check it
    setTimeout(() => {
      isMouseInteractionRef.current = false;
    }, 0);
  };

  const handleFocus = () => {
    // Only set focused path if focus wasn't triggered by mouse click
    if (!isMouseInteractionRef.current) {
      setFocusedPath(path);
    }
  };

  const toggleSection = () => {
    setOpenPaths((prev) => {
      const newSet = new Set(prev);
      const wasOpen = newSet.has(path);

      if (wasOpen) {
        newSet.delete(path);
      } else {
        newSet.add(path);
      }

      onVWBasicAccordionOpenClickEvent(
        trackingManager,
        section,
        !wasOpen,
        model
      );

      return newSet;
    });

    if (isIndexable) updateSection(section);
  };

  if (!hasSubsections) {
    const featureAppRoute = `/${model?.modelYear}/${model?.modelId}/${section.sectionId}`;
    const href =
      model?.modelYear && model?.modelId
        ? createFeatureHubHref(history, featureAppRoute, serverRequestUrl)
        : undefined;

    return (
      <NavSection>
        <NavLink
          tag='a'
          href={href}
          $level={level}
          onMouseEnter={() => setHoveredPath(path)}
          onMouseLeave={() => setHoveredPath(null)}
          onMouseDown={handleMouseDown}
          onFocus={handleFocus}
          onBlur={() => setFocusedPath(null)}
          onClick={(e) => {
            e.preventDefault();
            updateSection(section);
          }}
          style={{
            background: highlightBackground ? theme.colors.tab.stepnav.selected : 'transparent',
          }}
        >
          <TextBox
            appearance={TextAppearance.headline200}
            tag={TextTag.span}
            style={{
              [Breakpoints.default]: {
                fontSize: TextSize.TextSize16,
                lineHeight: TextLineHeight.TextLineHeight28,
              },
              [Breakpoints.b560]: {
                fontSize: TextSize.TextSize20,
                lineHeight: TextLineHeight.TextLineHeight32,
              }
            }}
            customStyle={(theme) => ({
              color: theme.colors.content.primary,
              fontWeight: highlightTab ? FontWeight.bold : FontWeight.regular,
            })}
          >
            {section.sectionName}
          </TextBox>
        </NavLink>
      </NavSection>
    );
  }

  return (
    <NavSection>
      <NavHeader
        $open={isOpen}
        $level={level}
        onClick={toggleSection}
        onMouseEnter={() => setHoveredPath(path)}
        onMouseLeave={() => setHoveredPath(null)}
        onMouseDown={handleMouseDown}
        onFocus={handleFocus}
        onBlur={() => setFocusedPath(null)}
        style={{
          background: highlightBackground ? theme.colors.tab.stepnav.selected : 'none',
        }}
      >
        <TextBox
          appearance={TextAppearance.headline200}
          tag={TextTag.span}
          style={{
            [Breakpoints.default]: {
              fontSize: TextSize.TextSize16,
              lineHeight: TextLineHeight.TextLineHeight28,
            },
            [Breakpoints.b560]: {
              fontSize: TextSize.TextSize20,
              lineHeight: TextLineHeight.TextLineHeight32,
            }
          }}
          customStyle={(theme) => ({
            color: theme.colors.content.primary,
            fontWeight: highlightTab ? FontWeight.bold : FontWeight.regular,
          })}
        >
          {section.sectionName}
        </TextBox>
        {hasSubsections && (
          <Arrow $open={isOpen}>
            <Icon
              name='ChevronRight'
              variant='default'
              style={(theme) => ({
                color: theme.colors.content.active,
                width: '18px',
              })}
            />
          </Arrow>
        )}
      </NavHeader>

      <NavList $open={isOpen} $level={level}>
        {section.subsections?.map((sub, i) => {
          const childPath = `${path}-${i}`;
          return (
            <NavItem
              key={sub.sectionId || i}
              section={sub}
              currentSection={currentSection}
              updateSection={updateSection}
              path={childPath}
              openPaths={openPaths}
              setOpenPaths={setOpenPaths}
              hoveredPath={hoveredPath}
              setHoveredPath={setHoveredPath}
              focusedPath={focusedPath}
              setFocusedPath={setFocusedPath}
              level={level + 1}
              trackingManager={trackingManager}
              model={model}
              serverRequestUrl={serverRequestUrl}
            />
          );
        })}
      </NavList>
    </NavSection>
  );
};
